import React, { useState } from 'react';
import AdminLayout from './AdminLayout';
import './Settings.css';

const Settings = () => {
  // General Settings State
  const [generalSettings, setGeneralSettings] = useState({
    platformName: 'SymbolFly',
    defaultCurrency: 'NGN (₦)',
    timezone: 'WAT (West Africa Time)',
    contactEmail: 'support@symbolfly.com'
  });

  // Payment Settings State
  const [paymentSettings, setPaymentSettings] = useState({
    paystackEnabled: true,
    flutterwaveEnabled: true,
    bankTransferEnabled: true,
    ussdEnabled: true,
    paystackApiKey: 'pk_test_xxxxxxxxxxxxx',
    flutterwaveApiKey: 'FLWSECK_TEST-xxxxxxxxxxxxx',
    transactionFee: '2.5'
  });

  // Security Settings State
  const [securitySettings, setSecuritySettings] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
    sessionTimeout: '30'
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: false
  });

  // Maintenance Settings State
  const [maintenanceSettings, setMaintenanceSettings] = useState({
    maintenanceMode: false,
    backupSchedule: 'daily'
  });

  const handleGeneralChange = (e) => {
    const { name, value } = e.target;
    setGeneralSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPaymentSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSecurityChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSecuritySettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotificationSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleMaintenanceChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMaintenanceSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveSettings = (e) => {
    e.preventDefault();
    // Placeholder for save settings
    console.log('Saving settings:', {
      general: generalSettings,
      payment: paymentSettings,
      security: securitySettings,
      notifications: notificationSettings,
      maintenance: maintenanceSettings
    });
    alert('Settings saved successfully!');
  };

  const handleCancel = () => {
    // Placeholder for cancel/reset
    console.log('Cancelled changes');
  };

  const handleDownloadLogs = () => {
    // Placeholder for download logs
    console.log('Downloading system logs...');
  };

  return (
    <AdminLayout pageTitle="Settings">
      <div className="admin-settings-page">
        {/* Page Title */}
        <div className="admin-settings-header">
          <h1 className="admin-settings-title">Settings</h1>
          <p className="admin-settings-subtitle">Manage platform configuration and preferences</p>
        </div>

        <form onSubmit={handleSaveSettings} className="admin-settings-form">
          {/* Settings Grid */}
          <div className="admin-settings-grid">
            {/* General Settings */}
            <div className="admin-settings-card">
              <div className="admin-settings-card-header">
                <h2 className="admin-settings-card-title">General Settings</h2>
              </div>
              <div className="admin-settings-card-content">
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Platform Name
                  </label>
                  <input
                    type="text"
                    name="platformName"
                    value={generalSettings.platformName}
                    onChange={handleGeneralChange}
                    className="admin-settings-input"
                  />
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Default Currency
                  </label>
                  <select
                    name="defaultCurrency"
                    value={generalSettings.defaultCurrency}
                    onChange={handleGeneralChange}
                    className="admin-settings-select"
                  >
                    <option value="NGN (₦)">NGN (₦)</option>
                    <option value="USD ($)">USD ($)</option>
                    <option value="GBP (£)">GBP (£)</option>
                    <option value="EUR (€)">EUR (€)</option>
                  </select>
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Timezone
                  </label>
                  <select
                    name="timezone"
                    value={generalSettings.timezone}
                    onChange={handleGeneralChange}
                    className="admin-settings-select"
                  >
                    <option value="WAT (West Africa Time)">WAT (West Africa Time)</option>
                    <option value="GMT (Greenwich Mean Time)">GMT (Greenwich Mean Time)</option>
                    <option value="EST (Eastern Standard Time)">EST (Eastern Standard Time)</option>
                    <option value="PST (Pacific Standard Time)">PST (Pacific Standard Time)</option>
                  </select>
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={generalSettings.contactEmail}
                    onChange={handleGeneralChange}
                    className="admin-settings-input"
                  />
                </div>
              </div>
            </div>

            {/* Payment Settings */}
            <div className="admin-settings-card">
              <div className="admin-settings-card-header">
                <h2 className="admin-settings-card-title">Payment Settings</h2>
              </div>
              <div className="admin-settings-card-content">
                <div className="admin-settings-field">
                  <label className="admin-settings-label">Payment Gateways</label>
                  <div className="admin-settings-checkbox-group">
                    <label className="admin-settings-checkbox-label">
                      <input
                        type="checkbox"
                        name="paystackEnabled"
                        checked={paymentSettings.paystackEnabled}
                        onChange={handlePaymentChange}
                        className="admin-settings-checkbox"
                      />
                      <span>Paystack</span>
                    </label>
                    <label className="admin-settings-checkbox-label">
                      <input
                        type="checkbox"
                        name="flutterwaveEnabled"
                        checked={paymentSettings.flutterwaveEnabled}
                        onChange={handlePaymentChange}
                        className="admin-settings-checkbox"
                      />
                      <span>Flutterwave</span>
                    </label>
                    <label className="admin-settings-checkbox-label">
                      <input
                        type="checkbox"
                        name="bankTransferEnabled"
                        checked={paymentSettings.bankTransferEnabled}
                        onChange={handlePaymentChange}
                        className="admin-settings-checkbox"
                      />
                      <span>Bank Transfer</span>
                    </label>
                    <label className="admin-settings-checkbox-label">
                      <input
                        type="checkbox"
                        name="ussdEnabled"
                        checked={paymentSettings.ussdEnabled}
                        onChange={handlePaymentChange}
                        className="admin-settings-checkbox"
                      />
                      <span>USSD</span>
                    </label>
                  </div>
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Paystack API Key
                  </label>
                  <input
                    type="password"
                    name="paystackApiKey"
                    value={paymentSettings.paystackApiKey}
                    onChange={handlePaymentChange}
                    className="admin-settings-input"
                    placeholder="pk_test_xxxxxxxxxxxxx"
                  />
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Flutterwave API Key
                  </label>
                  <input
                    type="password"
                    name="flutterwaveApiKey"
                    value={paymentSettings.flutterwaveApiKey}
                    onChange={handlePaymentChange}
                    className="admin-settings-input"
                    placeholder="FLWSECK_TEST-xxxxxxxxxxxxx"
                  />
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Transaction Fee (%)
                  </label>
                  <input
                    type="number"
                    name="transactionFee"
                    value={paymentSettings.transactionFee}
                    onChange={handlePaymentChange}
                    className="admin-settings-input"
                    step="0.1"
                    min="0"
                    max="10"
                  />
                </div>
              </div>
            </div>

            {/* Security Settings */}
            <div className="admin-settings-card">
              <div className="admin-settings-card-header">
                <h2 className="admin-settings-card-title">Security Settings</h2>
              </div>
              <div className="admin-settings-card-content">
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Current Password
                  </label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={securitySettings.currentPassword}
                    onChange={handleSecurityChange}
                    className="admin-settings-input"
                    placeholder="Enter current password"
                  />
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={securitySettings.newPassword}
                    onChange={handleSecurityChange}
                    className="admin-settings-input"
                    placeholder="Enter new password"
                  />
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={securitySettings.confirmPassword}
                    onChange={handleSecurityChange}
                    className="admin-settings-input"
                    placeholder="Confirm new password"
                  />
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-toggle-label">
                    <input
                      type="checkbox"
                      name="twoFactorEnabled"
                      checked={securitySettings.twoFactorEnabled}
                      onChange={handleSecurityChange}
                      className="admin-settings-toggle"
                    />
                    <span>Enable Two-Factor Authentication</span>
                  </label>
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Session Timeout (minutes)
                  </label>
                  <select
                    name="sessionTimeout"
                    value={securitySettings.sessionTimeout}
                    onChange={handleSecurityChange}
                    className="admin-settings-select"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">60 minutes</option>
                    <option value="120">2 hours</option>
                    <option value="240">4 hours</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="admin-settings-card">
              <div className="admin-settings-card-header">
                <h2 className="admin-settings-card-title">Notifications</h2>
              </div>
              <div className="admin-settings-card-content">
                <div className="admin-settings-field">
                  <label className="admin-settings-toggle-label">
                    <input
                      type="checkbox"
                      name="emailNotifications"
                      checked={notificationSettings.emailNotifications}
                      onChange={handleNotificationChange}
                      className="admin-settings-toggle"
                    />
                    <span>Enable Email Notifications</span>
                  </label>
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-toggle-label">
                    <input
                      type="checkbox"
                      name="smsNotifications"
                      checked={notificationSettings.smsNotifications}
                      onChange={handleNotificationChange}
                      className="admin-settings-toggle"
                    />
                    <span>Enable SMS Notifications (Nigeria only)</span>
                  </label>
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-toggle-label">
                    <input
                      type="checkbox"
                      name="pushNotifications"
                      checked={notificationSettings.pushNotifications}
                      onChange={handleNotificationChange}
                      className="admin-settings-toggle"
                    />
                    <span>Enable Push Notifications</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Maintenance Settings */}
            <div className="admin-settings-card">
              <div className="admin-settings-card-header">
                <h2 className="admin-settings-card-title">Maintenance / System</h2>
              </div>
              <div className="admin-settings-card-content">
                <div className="admin-settings-field">
                  <label className="admin-settings-toggle-label">
                    <input
                      type="checkbox"
                      name="maintenanceMode"
                      checked={maintenanceSettings.maintenanceMode}
                      onChange={handleMaintenanceChange}
                      className="admin-settings-toggle"
                    />
                    <span>Enable Maintenance Mode</span>
                  </label>
                  <p className="admin-settings-hint">
                    When enabled, the platform will be unavailable to users
                  </p>
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">
                    Backup Schedule
                  </label>
                  <select
                    name="backupSchedule"
                    value={maintenanceSettings.backupSchedule}
                    onChange={handleMaintenanceChange}
                    className="admin-settings-select"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div className="admin-settings-field">
                  <label className="admin-settings-label">System Logs</label>
                  <button
                    type="button"
                    className="admin-settings-download-button"
                    onClick={handleDownloadLogs}
                  >
                    📥 Download System Logs
                  </button>
                  <p className="admin-settings-hint">
                    Download system logs for troubleshooting
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="admin-settings-actions">
            <button
              type="button"
              className="admin-settings-button admin-settings-button-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-settings-button admin-settings-button-primary"
            >
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default Settings;

